import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TdeeEstimate, TdeeStatus } from '@nutrigeist/shared';
import { ApiError } from '../api/client.js';
import { authApi, tdeeApi } from '../api/endpoints.js';
import { useAuth, useCurrentUser } from '../auth/AuthContext.js';
import { formatKcal, formatNumber } from '../lib/format.js';

/** Why no number is shown, in the user's terms rather than the code's. */
const REASONS: Record<Exclude<TdeeStatus, 'ok' | 'disabled'>, string> = {
  insufficient_window:
    'Der Zeitraum ist zu kurz. Unter zwei Wochen misst die Rechnung vor allem deinen Wasserhaushalt.',
  insufficient_logging:
    'Zu viele Tage ohne Einträge. Der Schnitt müsste für die nicht erfassten Tage raten, und genau das soll er nicht.',
  insufficient_weight_data:
    'Zu wenige Wiegungen im Zeitraum. Ohne eine belastbare Trendlinie ist nicht zu erkennen, wohin das Gewicht geht.',
  implausible:
    'Das Ergebnis liegt außerhalb dessen, was ein Körper verbrauchen kann. Meist steckt eine falsch erfasste Menge oder eine Fehlwiegung dahinter.',
};

/**
 * Measured maintenance calories, or a plain reason there are none yet.
 *
 * The reason matters as much as the number. A maintenance figure carries enough
 * authority that a wrong one gets eaten to for weeks, so the honest failure
 * modes are shown as prominently as a result would be.
 */
export function TdeeCard({ days }: { days: number }): JSX.Element | null {
  const user = useCurrentUser();
  const { setUser } = useAuth();
  const queryClient = useQueryClient();

  const enabled = user.preferences.adaptiveTdee ?? false;

  // The API clamps below 14 days; asking for a 7-day window would only ever
  // return `insufficient_window`, so widen it rather than show that.
  const windowDays = Math.max(28, days);

  const query = useQuery({
    queryKey: ['tdee', windowDays],
    queryFn: () => tdeeApi.estimate(windowDays),
    enabled,
  });

  const adopt = useMutation({
    mutationFn: (kcal: number) => authApi.updateProfile({ goals: { kcal } }),
    onSuccess: async (updated) => {
      setUser(updated);
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
    },
  });

  if (!enabled) return null;

  const estimate: TdeeEstimate | undefined = query.data;

  return (
    <div className="card stack">
      <div className="card-title">Kalorienverbrauch</div>

      {query.isLoading && <span className="hint">Wird berechnet …</span>}

      {query.isError && (
        <span className="hint">
          {query.error instanceof ApiError ? query.error.message : 'Konnte nicht berechnet werden.'}
        </span>
      )}

      {estimate && estimate.status === 'ok' && (
        <>
          <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: '1.6rem' }}>{formatKcal(estimate.tdeeKcal!)}</strong>
            <span className="hint">pro Tag, gemessen</span>
          </div>

          <span className="hint">
            Aus {formatKcal(estimate.averageIntakeKcal!)} Schnitt an {estimate.loggedDays} von{' '}
            {estimate.windowDays} Tagen und {estimate.trendChangeKg! >= 0 ? '+' : '−'}
            {formatNumber(Math.abs(estimate.trendChangeKg!), 2)} kg Trendgewicht.
          </span>

          {estimate.suggestedGoalKcal != null && (
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                Vorschlag fürs Ziel: <strong>{formatKcal(estimate.suggestedGoalKcal)}</strong>
              </span>
              {user.goals.kcal !== estimate.suggestedGoalKcal && (
                <button
                  type="button"
                  className="secondary"
                  disabled={adopt.isPending}
                  onClick={() => adopt.mutate(estimate.suggestedGoalKcal!)}
                >
                  Übernehmen
                </button>
              )}
            </div>
          )}

          {adopt.isSuccess && <span className="hint">Als Tagesziel gespeichert.</span>}
        </>
      )}

      {estimate && estimate.status !== 'ok' && estimate.status !== 'disabled' && (
        <>
          <span className="hint">{REASONS[estimate.status]}</span>
          {estimate.loggedDays > 0 && (
            <span className="hint">
              Bisher {estimate.loggedDays} von {estimate.windowDays} Tagen erfasst.
            </span>
          )}
        </>
      )}
    </div>
  );
}
